// Single caption renderer shared by live preview and burned-in export.
import { capWords } from "./lines";
const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
const easeOut = (p) => 1 - Math.pow(1 - p, 3);
const easeBack = (p) => 1 + 2.7 * Math.pow(p - 1, 3) + 1.7 * Math.pow(p - 1, 2);
const easeBounce = (p) => {
  const n = 7.5625, d = 2.75;
  if (p < 1 / d) return n * p * p;
  if (p < 2 / d) return n * (p -= 1.5 / d) * p + 0.75;
  if (p < 2.5 / d) return n * (p -= 2.25 / d) * p + 0.9375;
  return n * (p -= 2.625 / d) * p + 0.984375;
};

export function findCaption(caps, t) {
  for (const c of caps) if (t >= c.start && t < c.end) return c;
  return null;
}

export const wordTimes = capWords;

const hexA = (hex, a) => {
  const h = hex.replace("#", "");
  const v = parseInt(h.length === 3 ? h.split("").map((x) => x + x).join("") : h, 16);
  return `rgba(${(v >> 16) & 255},${(v >> 8) & 255},${v & 255},${a})`;
};

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
  ctx.fill();
}

export function drawCaption(ctx, W, H, cap, t, st) {
  if (!cap) return;
  let words = wordTimes(cap);
  if (!words.length) return;
  let ai = 0;
  words.forEach((w, i) => { if (t >= w.s) ai = i; });
  let t0 = cap.start;
  if (st.chunk > 0) {
    const g = Math.floor(ai / st.chunk);
    words = words.slice(g * st.chunk, g * st.chunk + st.chunk);
    ai -= g * st.chunk;
    t0 = words[0].s;
  }
  const A = st.animation;
  const local = t - t0;
  const p = clamp(local / 0.35);
  const fs = Math.max(8, Math.round((Math.min(W, H) * st.size) / 100));
  ctx.font = `${st.weight} ${fs}px "${st.font}", "Noto Sans Devanagari", sans-serif`;
  ctx.textBaseline = "middle";
  ctx.textAlign = "left";
  const label = (w) => (st.uppercase ? w.toUpperCase() : w);
  const space = ctx.measureText(" ").width;
  const maxW = W * 0.86;
  const lines = [[]];
  let lw = 0;
  words.forEach((w, i) => {
    const width = ctx.measureText(label(w.w)).width;
    if (lw > 0 && lw + space + width > maxW) { lines.push([]); lw = 0; }
    lines[lines.length - 1].push({ ...w, i, width });
    lw += (lw > 0 ? space : 0) + width;
  });
  const lineH = fs * 1.3;
  const blockH = lines.length * lineH;
  const y0 = st.position === "top" ? H * 0.12 : st.position === "middle" ? (H - blockH) / 2 : H * 0.84 - blockH;
  const cx = W / 2;
  const cy = y0 + blockH / 2;

  let visibleChars = Infinity;
  if (A === "typewriter") {
    const totalChars = words.reduce((a, w) => a + w.w.length, 0);
    const span = st.chunk > 0 ? words[words.length - 1].e - t0 : cap.end - cap.start;
    visibleChars = Math.ceil(totalChars * clamp(local / Math.max(0.3, span * 0.7)));
  }

  ctx.save();
  let alpha = 1, sc = 1, sx = 1, dx = 0, dy = 0, rot = 0;
  if (A === "fade") alpha = p;
  else if (A === "pop") sc = 0.6 + 0.4 * easeBack(p);
  else if (A === "zoom-in") { sc = 1.5 - 0.5 * easeOut(p); alpha = p; }
  else if (A === "zoom-out") sc = 0.3 + 0.7 * easeOut(p);
  else if (A === "slide-up") { dy = (1 - easeOut(p)) * fs * 1.2; alpha = p; }
  else if (A === "slide-down") { dy = -(1 - easeOut(p)) * fs * 1.2; alpha = p; }
  else if (A === "slide-left") { dx = (1 - easeOut(p)) * W * 0.15; alpha = p; }
  else if (A === "slide-right") { dx = -(1 - easeOut(p)) * W * 0.15; alpha = p; }
  else if (A === "bounce") dy = -Math.abs(Math.sin(p * Math.PI * 2)) * (1 - p) * fs * 0.8;
  else if (A === "elastic") sc = 1 + Math.sin(p * Math.PI * 3) * (1 - p) * 0.35;
  else if (A === "drop") dy = -(1 - easeBounce(p)) * fs * 1.6;
  else if (A === "rotate-in") { rot = (1 - easeOut(p)) * -0.25; alpha = p; }
  else if (A === "blur-in") { ctx.filter = `blur(${((1 - p) * fs) / 6}px)`; alpha = p; }
  else if (A === "stretch") sx = 1.6 - 0.6 * easeOut(p);
  else if (A === "shake") dx = Math.sin(local * 55) * fs * 0.035;
  else if (A === "flicker") alpha = p < 1 ? (Math.floor(local * 25) % 2 ? 0.25 : 1) : 1;
  ctx.globalAlpha = clamp(alpha);
  ctx.translate(cx + dx, cy + dy);
  ctx.rotate(rot);
  ctx.scale(sc * sx, sc);
  ctx.translate(-cx, -cy);

  const glow = A === "glow" ? st.shadowBlur * (0.7 + 0.3 * Math.sin(local * 5)) : st.shadowBlur;
  const pad = fs * 0.3;
  if (st.bgMode === "box") {
    const bw = Math.max(...lines.map((l) => l.reduce((a, w, k) => a + w.width + (k ? space : 0), 0)));
    ctx.fillStyle = hexA(st.bgColor, st.bgOpacity);
    roundRect(ctx, cx - bw / 2 - pad * 1.5, y0 - pad, bw + pad * 3, blockH + pad * 2, fs * 0.25);
  }
  let charCount = 0;
  lines.forEach((line, li) => {
    const total = line.reduce((a, w, k) => a + w.width + (k ? space : 0), 0);
    let x = cx - total / 2;
    const y = y0 + li * lineH + lineH / 2;
    if (st.bgMode === "line") {
      ctx.fillStyle = hexA(st.bgColor, st.bgOpacity);
      roundRect(ctx, x - pad, y - lineH / 2 + 2, total + pad * 2, lineH - 4, fs * 0.18);
    }
    line.forEach((w) => {
      const txtFull = label(w.w);
      let txt = txtFull;
      if (charCount >= visibleChars) txt = "";
      else if (charCount + txtFull.length > visibleChars) txt = txtFull.slice(0, visibleChars - charCount);
      charCount += txtFull.length;
      const active = w.i === ai && t >= w.s;
      const done = w.i <= ai;
      if (A === "word-reveal" && !done) { x += w.width + space; return; }
      let color = st.color;
      if ((A === "karaoke" && done) || ((A === "word-color" || A === "word-pop") && active)) color = st.highlightColor;
      ctx.save();
      let wy = y;
      if (A === "wave") wy += Math.sin(local * 6 + w.i * 0.9) * fs * 0.08;
      if (A === "word-box" && active) {
        ctx.fillStyle = st.highlightColor;
        roundRect(ctx, x - fs * 0.12, y - fs * 0.62, w.width + fs * 0.24, fs * 1.24, fs * 0.18);
      }
      if (A === "word-pop" && active) {
        const k = 1 + 0.18 * easeBack(clamp((t - w.s) / 0.2));
        ctx.translate(x + w.width / 2, y);
        ctx.scale(k, k);
        ctx.translate(-(x + w.width / 2), -y);
      }
      if (txt) {
        ctx.shadowColor = st.shadowColor;
        ctx.shadowBlur = glow;
        ctx.shadowOffsetY = (st.shadowY * fs) / 40;
        if (st.outlineWidth > 0) {
          ctx.lineJoin = "round";
          ctx.lineWidth = (fs * st.outlineWidth) / 40;
          ctx.strokeStyle = st.outlineColor;
          ctx.strokeText(txt, x, wy);
          ctx.shadowColor = "transparent";
        }
        ctx.fillStyle = A === "word-box" && active && st.color.toUpperCase() === st.highlightColor.toUpperCase() ? "#000" : color;
        ctx.fillText(txt, x, wy);
      }
      ctx.restore();
      x += w.width + space;
    });
  });
  ctx.restore();
}
